/* eslint-disable camelcase */
import React from "react";
import { useSelector } from "react-redux";
import { object, array, bool, string } from "prop-types";
import get from "lodash/get";
import { Link } from "@quintype/components";
import CollectionTitle from "../../atoms/CollectionTitle";
import { HeroImage } from "../../atoms/HeroImage";
import { getStoryHeadline, getStoryLabel, isExternalStory } from "../../utils/story";
import { PublishAndReadTime } from "../../atoms/PublishAndReadTime";
import { useDeviceType } from "../../custom-hooks/useDeviceType";
import { DfpComponent } from "../../ads/dfp-component";
import { OtherStory } from "./OtherStory";
import { Label } from "../../atoms/Label";
import Headline from "../../atoms/Headline";
import SubHeadline from "../../atoms/SubHeadline";
import Newsletter from "../../atoms/Newsletter";
import { RowSeparator } from "../../atoms/RowSeparator";

import "./three-col-seven-stories-var-one.m.css";

const AdSlot = ({ width, height, showNewsletter, row_ad_details, collection }) => {
  const rowAdConfig1 = useSelector((state) =>
    get(state, ["qt", "config", "ads-config", "slots", `row_ad_story_300x250`], {})
  );
  const rowAdConfig2 = useSelector((state) =>
    get(state, ["qt", "config", "ads-config", "slots", `row_ad_story_300x600`], {})
  );
  const pageType = useSelector((state) => get(state, ["qt", "pageType"], null));
  if (!showNewsletter) {
    return (
      <div>
        <div style={{ maxWidth: "350px", width, height, backgroundColor: "#eff1f2", margin: "8px 25px" }}></div>
        {height == 250 ? (
          <DfpComponent
            adStyleName={`three-col-seven-var-one-ad-slot-size-${width}x${height}`}
            id={`${pageType}-${collection.slug}-row-ad-1`}
            path={row_ad_details}
            size={rowAdConfig1.sizes}
            viewPortSizeMapping={rowAdConfig1.view_port_size_mapping}
          />
        ) : (
          <DfpComponent
            adStyleName={`three-col-seven-var-one-ad-slot-size-${width}x${height}`}
            id={`${pageType}-${collection.slug}-row-ad-2`}
            path={row_ad_details}
            size={rowAdConfig2.sizes}
            viewPortSizeMapping={rowAdConfig2.view_port_size_mapping}
          />
        )}
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
      <DfpComponent
        adStyleName="three-col-seven-var-one-ad-slot-size-300x250"
        id={`${pageType}-${collection.slug}-row-ad-3`}
        path={row_ad_details}
        size={rowAdConfig1.sizes}
        viewPortSizeMapping={rowAdConfig1.view_port_size_mapping}
      />
      <Newsletter customStyle={{ marginTop: "16px" }} />
    </div>
  );
};
AdSlot.propTypes = {
  width: string,
  height: string,
  showNewsletter: bool,
  row_ad_details: string,
  collection: object,
};

const HeroStory = ({ story, showSubheadline, showReadTime, showTimestamp, showSeparator, sectionSlug }) => {
  const headline = getStoryHeadline(story);
  const label = getStoryLabel(story);
  const externalStoryUrl = isExternalStory(story);
  const href = externalStoryUrl || `/${story?.slug}`;
  const target = externalStoryUrl ? "_blank" : "_self";
  return (
    <Link href={href} target={target}>
      <div styleName="main-story" style={{ border: `${showSeparator || "none"}` }}>
        {label && <Label story={story} />}

        {headline && <Headline styleName="headline" headline={headline} sectionSlug={sectionSlug} />}
        {showSubheadline && story?.subheadline && (
          <SubHeadline styleName="subheadline" subHeadline={story?.subheadline} sectionSlug={sectionSlug} />
        )}
        <PublishAndReadTime story={story} showReadTime={showReadTime} showTimestamp={showTimestamp} />
        <HeroImage story={story} aspectRatio={[[4, 3]]} iconSizes={{ height: 48, width: 48 }} />
      </div>
    </Link>
  );
};

HeroStory.propTypes = {
  story: object,
  showSubheadline: bool,
  showReadTime: bool,
  showTimestamp: bool,
  showSeparator: bool,
  sectionSlug: string,
};

export const ThreeColSevenStoriesVarOne = ({ collection, stories, extraProp = "" }) => {
  if (stories.length < 1) {
    return null;
  }

  const {
    show_timestamp,
    show_read_time,
    subheadline_of_first_story,
    show_row_title,
    show_column_separator,
    show_story_separator,
    show_row_separator,
    enable_newsletter,
    enable_sponsor_ad,
    sponsor_ad_details,
    enable_row_ad,
    row_ad_details,
    show_row_ad_unit_bottom,
  } = get(collection, ["associated-metadata"], {});
  const bottomAdConfig = useSelector((state) =>
    get(state, ["qt", "config", "ads-config", "slots", "row_bottom_ad"], {})
  );
  const pageType = useSelector((state) => get(state, ["qt", "pageType"], null));
  const deviceType = useDeviceType();
  const { height, width } =
    deviceType === "mobile" || (deviceType === "desktop" && enable_newsletter)
      ? { height: "250", width: "300" }
      : { height: "600", width: "300" };
  const sponsorAdConfig = useSelector((state) => get(state, ["qt", "config", "ads-config", "slots", "sponsor_ad"], {}));
  const sectionSlug = extraProp;
  return (
    <div className="row-component" styleName="three-col-seven-stories-var-one">
      {show_row_title && (
        <div styleName="collection-title-container">
          <CollectionTitle
            collection={collection}
            enableSponsorAd={enable_sponsor_ad}
            sponsorAdDetails={sponsor_ad_details}
            sectionSlug={sectionSlug}
            sponsorAdConfig={sponsorAdConfig}
          />
        </div>
      )}
      <div styleName="columns">
        <div
          styleName="stories-column"
          style={{
            borderBottom: `${show_story_separator || "none"}`,
          }}
        >
          <div
            styleName="first-column"
            style={{
              border: `${
                (deviceType !== "mobile" && show_column_separator) ||
                (deviceType === "mobile" && show_story_separator) ||
                "none"
              }`,
            }}
          >
            <HeroStory
              story={stories[0]}
              showSubheadline={subheadline_of_first_story}
              showReadTime={show_read_time}
              showTimestamp={show_timestamp}
              showSeparator={show_story_separator}
              sectionSlug={sectionSlug}
            />
          </div>
          <div styleName="remaining-columns" style={{ border: `${show_column_separator || "none"}` }}>
            <div styleName="second-column">
              {stories.slice(1, 3).map((story, index) => (
                <OtherStory
                  key={story.id}
                  story={story}
                  showReadTime={show_read_time}
                  showTimestamp={show_timestamp}
                  showBorder={show_story_separator && index !== stories.length - 1}
                  sectionSlug={sectionSlug}
                />
              ))}
            </div>
            {deviceType === "mobile" && enable_row_ad && (
              <div styleName="ad-wrapper">
                <AdSlot
                  showNewsletter={false}
                  height={height}
                  width={width}
                  rowAdDetails={row_ad_details}
                  deviceType={deviceType}
                  row_ad_details={row_ad_details}
                  collection={collection}
                />
              </div>
            )}
            <div styleName="third-column" style={{ border: show_story_separator || "none" }}>
              {stories.slice(3, 7).map((story, index) => (
                <OtherStory
                  key={story.id}
                  story={story}
                  showReadTime={show_read_time}
                  showTimestamp={show_timestamp}
                  showBorder={show_story_separator && index + 3 !== stories.length - 1}
                  sectionSlug={sectionSlug}
                />
              ))}
            </div>
          </div>
        </div>
        {deviceType !== "mobile" && enable_row_ad && (
          <div styleName="ad-wrapper" style={{ border: `${show_column_separator || "none"}` }}>
            <AdSlot
              showNewsletter={deviceType === "desktop" && enable_newsletter}
              height={height}
              width={width}
              rowAdDetails={row_ad_details}
              deviceType={deviceType}
              row_ad_details={row_ad_details}
              collection={collection}
            />
          </div>
        )}
      </div>
      {show_row_separator && <RowSeparator />}
      {show_row_ad_unit_bottom && (
        <DfpComponent
          adStyleName="ad-slot-size-300x250"
          id={`${pageType}-${collection.slug}-row-bottom-ad`}
          path={show_row_ad_unit_bottom}
          size={bottomAdConfig.sizes}
          viewPortSizeMapping={bottomAdConfig.view_port_size_mapping}
        />
      )}
    </div>
  );
};

ThreeColSevenStoriesVarOne.propTypes = {
  collection: object,
  stories: array,
  extraProp: string,
};

ThreeColSevenStoriesVarOne.storyLimit = 7;
